import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Spinner, Badge, Modal, InputGroup, ProgressBar, Dropdown, ButtonGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api';

function BayarJasaMedis() {
  const navigate = useNavigate();

  // ── Helper Tanggal Hari Ini (Format DD/MM/YYYY) ──
  const getTodayDmy = () => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  // ── States Utama ──
  const [dataJasa, setDataJasa] = useState([]);
  const [listPeriode, setListPeriode] = useState([]);
  const [selectedPeriode, setSelectedPeriode] = useState(getTodayDmy());
  const datePickerRef = useRef(null);
  const [searchDokter, setSearchDokter] = useState('');
  const [selectedRow, setSelectedRow] = useState(null); // Dokter terpilih
  const [isLoading, setIsLoading] = useState(false);
  const [time, setTime] = useState({ date: '', clock: '' });

  // ── Pagination ──
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // ── Modal Perhitungan dan Laporan Pajak Dokter ──
  const [showModalPajak, setShowModalPajak] = useState(false);
  const [pajakPeriode, setPajakPeriode] = useState('');
  const [lokasiFileLaporan, setLokasiFileLaporan] = useState('');
  const [isProcessingPajak, setIsProcessingPajak] = useState(false);
  const [pajakProgress, setPajakProgress] = useState(0);
  const [pajakResult, setPajakResult] = useState(null);

  // ── Modal Rincian Jasa Dokter ──
  const [showModalRincian, setShowModalRincian] = useState(false);
  const [listRincian, setListRincian] = useState([]);
  const [isLoadingRincian, setIsLoadingRincian] = useState(false);
  const [searchRincian, setSearchRincian] = useState('');

  // ── Modal Slip Jasa Dokter ──
  const [showModalSlip, setShowModalSlip] = useState(false);
  const [showModalPilihSlip, setShowModalPilihSlip] = useState(false);

  // ── Modal Cetak PDF Laporan ──
  const [showModalPdf, setShowModalPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');
  const [pdfFileName, setPdfFileName] = useState('dokumen.pdf');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // ── Progress Modal ──
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressStatusText, setProgressStatusText] = useState('');

  // ── Modern MessageBox State ──
  const [messageBox, setMessageBox] = useState({
    show: false,
    type: 'info', // 'warning' | 'danger' | 'success' | 'info' | 'confirm'
    title: '',
    message: '',
    confirmText: 'OK, Mengerti',
    cancelText: 'Batal',
    onConfirm: null,
    onCancel: null
  });

  const showMsg = (message, type = 'warning', title = null, onConfirm = null) => {
    let defaultTitle = 'Informasi';
    if (type === 'warning') defaultTitle = 'Peringatan';
    else if (type === 'danger' || type === 'error') defaultTitle = 'Terjadi Kesalahan';
    else if (type === 'success') defaultTitle = 'Berhasil';
    else if (type === 'confirm') defaultTitle = 'Konfirmasi Tindakan';

    setMessageBox({
      show: true,
      type: type === 'error' ? 'danger' : type,
      title: title || defaultTitle,
      message,
      confirmText: type === 'confirm' ? 'Ya, Lanjutkan' : 'OK, Mengerti',
      cancelText: 'Batal',
      onConfirm: onConfirm || null,
      onCancel: null
    });
  };

  const showConfirm = (message, onConfirm, title = 'Konfirmasi Tindakan') => {
    showMsg(message, 'confirm', title, onConfirm);
  };

  const closeMsg = () => {
    setMessageBox(prev => ({ ...prev, show: false }));
  };

  // Live Digital Clock
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTime({
        date: now.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        clock: now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) + ' WIB'
      });
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Format Angka Rupiah
  const formatRp = (val) => {
    if (val === undefined || val === null || isNaN(val)) return '0';
    return new Intl.NumberFormat('id-ID').format(Math.round(val));
  };

  // ── Helper Konversi Tanggal untuk Input Calendar (HTML5 type="date") ──
  const dmyToYmd = (dmy) => {
    if (!dmy) return '';
    const parts = dmy.split('/');
    if (parts.length === 3) {
      const [d, m, y] = parts;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return dmy;
  };

  const ymdToDmy = (ymd) => {
    if (!ymd) return '';
    const parts = ymd.split('-');
    if (parts.length === 3) {
      const [y, m, d] = parts;
      return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
    }
    return ymd;
  };

  // ── Fetch Data Jasa Medis ──
  const fetchJasaMedis = async (periodeParam = null) => {
    setIsLoading(true);
    const targetPeriode = (periodeParam !== null && periodeParam !== undefined) ? periodeParam : (selectedPeriode || getTodayDmy());

    try {
      const res = await fetch(`${getApiUrl()}/show_jasamedis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ periode: targetPeriode })
      });

      if (!res.ok) {
        throw new Error(`Server status ${res.status}: Gagal memuat data jasa medis`);
      }

      const json = await res.json();
      const rawList = json.list_jasamedis || [];
      setDataJasa(rawList);

      if (json.list_periode && Array.isArray(json.list_periode)) {
        setListPeriode(json.list_periode);
      }

      // Jika ada periodeParam eksplisit yang diminta, sinkronkan ke selectedPeriode
      if (periodeParam) {
        setSelectedPeriode(periodeParam);
      }

      // Reset row selection jika tidak ada dalam data baru
      if (selectedRow) {
        const stillExists = rawList.find(r => r.kd_dokter === selectedRow.kd_dokter);
        if (stillExists) setSelectedRow(stillExists);
        else setSelectedRow(null);
      }

    } catch (err) {
      console.error(err);
      showMsg('Gagal mengambil data jasa dokter: ' + err.message, 'danger', 'Koneksi Gagal');
    } finally {
      setIsLoading(false);
    }
  };

  // Muat data awal saat komponen dibuka (default tanggal hari ini)
  useEffect(() => {
    fetchJasaMedis(getTodayDmy());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handler Ubah Tanggal / Periode via Input Kalender ──
  const handleCalendarChange = (ymdValue) => {
    if (!ymdValue) return;
    const dmy = ymdToDmy(ymdValue);
    setSelectedPeriode(dmy);
    fetchJasaMedis(dmy);
  };

  // ── Filter Data Berdasarkan Pencarian Dokter ──
  const filteredData = useMemo(() => {
    if (!searchDokter) return dataJasa;
    const q = searchDokter.toLowerCase();
    return dataJasa.filter(item => 
      (item.nm_dokter && item.nm_dokter.toLowerCase().includes(q)) ||
      (item.kd_dokter && item.kd_dokter.toLowerCase().includes(q))
    );
  }, [dataJasa, searchDokter]);

  // ── Ringkasan Total Metrik ──
  const summaryTotals = useMemo(() => {
    let totTarif = 0;
    let totKoreksiTarif = 0;
    let totJasa = 0;
    let totKoreksiJasa = 0;
    let totNonJasa = 0;
    let totKoreksiNonJasa = 0;
    let totJumlah = 0;
    let totDibayar = 0;
    let totNonJasa2 = 0;
    let totPajak = 0;
    let totPinjaman = 0;
    let totDibayar2 = 0;

    filteredData.forEach(item => {
      totTarif += Number(item.tarif || 0);
      totKoreksiTarif += Number(item.koreksitarif || 0);
      totJasa += Number(item.jasa || 0);
      totKoreksiJasa += Number(item.koreksijasa || 0);
      totNonJasa += Number(item.nonjasa || 0);
      totKoreksiNonJasa += Number(item.koreksinonjasa || 0);
      totJumlah += Number(item.jumlah || 0);
      totDibayar += Number(item.dibayar || 0);
      totNonJasa2 += Number(item.nonjasa2 || 0);
      totPajak += Number(item.pajak || 0);
      totPinjaman += Number(item.pinjaman || 0) + Number(item.koreksipinjaman || 0);
      totDibayar2 += Number(item.dibayar2 || 0);
    });

    return {
      totTarif,
      totKoreksiTarif,
      totJasa,
      totKoreksiJasa,
      totNonJasa,
      totKoreksiNonJasa,
      totJumlah,
      totDibayar,
      totNonJasa2,
      totPajak,
      totPinjaman,
      totDibayar2,
      count: filteredData.length
    };
  }, [filteredData]);

  // ── Reset page saat filter berubah ──
  useEffect(() => { setCurrentPage(1); }, [filteredData]);

  // ── Data halaman aktif ──
  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
  const pagedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // ── Handler Klik Tombol Rincian ──
  const handleOpenRincian = async () => {
    if (!selectedRow) {
      showMsg('Pilih salah satu baris dokter pada tabel terlebih dahulu!', 'warning', 'Pilih Dokter');
      return;
    }

    setShowModalRincian(true);
    setIsLoadingRincian(true);
    setSearchRincian('');

    try {
      const res = await fetch(`${getApiUrl()}/rincian_jasa_dokter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kd_dokter: selectedRow.kd_dokter
        })
      });

      if (!res.ok) {
        throw new Error('Gagal mengambil rincian tindakan jasa dokter');
      }

      const json = await res.json();
      const items = json.data || json.list_rincian || [];
      setListRincian(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error(err);
      showMsg('Gagal memuat rincian tindakan: ' + err.message, 'danger', 'Kesalahan Data');
      setListRincian([]);
    } finally {
      setIsLoadingRincian(false);
    }
  };

  // Filter Rincian Dokter
  const filteredRincian = useMemo(() => {
    if (!searchRincian) return listRincian;
    const q = searchRincian.toLowerCase();
    return listRincian.filter(r => 
      (r.nm_pasien && r.nm_pasien.toLowerCase().includes(q)) ||
      (r.no_medrec && r.no_medrec.toLowerCase().includes(q)) ||
      (r.nm_tindakan && r.nm_tindakan.toLowerCase().includes(q)) ||
      (r.no_nota && r.no_nota.toLowerCase().includes(q)) ||
      (r.no_kuitansi && r.no_kuitansi.toLowerCase().includes(q))
    );
  }, [listRincian, searchRincian]);

  // ── Handler Klik Tombol Slip Dokter (Buka Modal Opsi Cetak Slip) ──
  const handleOpenSlip = () => {
    setShowModalPilihSlip(true);
  };

  // Print Slip Dialog
  const handlePrintSlipWindow = () => {
    window.print();
  };

  // ── Handler Buka Dialog Perhitungan dan Laporan Pajak Dokter ──
  const handleBukaDialogPajak = () => {
    const p = selectedPeriode || getTodayDmy();
    setPajakPeriode(p);
    setLokasiFileLaporan(`C:\\LAPORAN\\PAJAK_DOKTER_${p.replace(/\//g, '')}.pdf`);
    setPajakProgress(0);
    setPajakResult(null);
    setShowModalPajak(true);
  };

  // ── Handler Jalankan Eksekusi Proses Pajak (isilaporan + hitungpajak) ──
  const handleJalankanProsesPajak = async () => {
    if (!pajakPeriode) {
      showMsg('Periode pembayaran jasa harus diisi terlebih dahulu!', 'warning', 'Periode Kosong');
      return;
    }

    setIsProcessingPajak(true);
    setPajakProgress(10);

    // Animasi progress bar menyerupai ProgressBar1 di VB
    const progressTimer = setInterval(() => {
      setPajakProgress(prev => {
        if (prev >= 90) return prev;
        return prev + 15;
      });
    }, 250);

    try {
      const res = await fetch(`${getApiUrl()}/proses_pajak_dokter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ periode: pajakPeriode })
      });

      clearInterval(progressTimer);

      if (!res.ok) {
        const errJson = await res.json().catch(() => null);
        throw new Error(errJson?.message || `Server status ${res.status}: Gagal memproses pajak`);
      }

      const json = await res.json();
      setPajakProgress(100);
      setPajakResult(json);

      // Sinkronkan ke state utama dan muat ulang tabel
      setSelectedPeriode(pajakPeriode);
      fetchJasaMedis(pajakPeriode);

      showMsg(
        `Perhitungan pajak dokter periode ${pajakPeriode} selesai disimulasikan dan disimpan!\n\n` +
        `• Dokter Diproses: ${json.count} Dokter\n` +
        `• Total PPh 21 Periode Ini: Rp. ${new Intl.NumberFormat('id-ID').format(json.total_pajak || 0)}`,
        'success',
        'Perhitungan Pajak Sukses'
      );

    } catch (err) {
      clearInterval(progressTimer);
      setPajakProgress(0);
      console.error(err);
      showMsg('Terjadi kesalahan saat memproses pajak: ' + err.message, 'danger', 'Gagal Memproses Pajak');
    } finally {
      setIsProcessingPajak(false);
    }
  };

  // ── Handler Cetak Laporan Pajak Dokter PDF ──
  const handleCetakLaporanPajakPdf = async () => {
    const p = pajakPeriode || selectedPeriode || getTodayDmy();
    try {
      setIsGeneratingPdf(true);
      const res = await fetch(`${getApiUrl()}/cetak_laporan_pajak_dokter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ periode: p })
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => null);
        throw new Error(errJson?.message || `Server status ${res.status}: Gagal mencetak laporan`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfTitle(`Laporan Pajak Penghasilan Dokter (PPh 21) - Periode ${p}`);
      setPdfFileName(`Laporan_Pajak_Dokter_${p.replace(/\//g, '_')}.pdf`);
      setShowModalPdf(true);
    } catch (err) {
      console.error(err);
      showMsg('Gagal menghasilkan Laporan Pajak Dokter: ' + err.message, 'danger', 'Kesalahan Cetak');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // ── Handler Cetak Laporan PDF ──
  const handleCetakLaporan = async () => {
    if (dataJasa.length === 0) {
      showMsg('Tidak ada data jasa dokter untuk dicetak pada periode ini!', 'warning', 'Data Kosong');
      return;
    }

    setIsGeneratingPdf(true);
    setShowProgressModal(true);
    setProgressPercent(20);
    setProgressStatusText('Menyiapkan dokumen Rekap Jasa Medis Dokter...');

    let pVal = 20;
    const timer = setInterval(() => {
      pVal = Math.min(pVal + 20, 85);
      setProgressPercent(pVal);
      if (pVal > 40 && pVal <= 70) {
        setProgressStatusText('Mengompilasi tarif, jasa, dan potongan pajak...');
      } else if (pVal > 70) {
        setProgressStatusText('Menyusun halaman PDF dengan nomor halaman...');
      }
    }, 200);

    try {
      const res = await fetch(`${getApiUrl()}/cetak_laporan_jasamedis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ periode: selectedPeriode })
      });

      if (!res.ok) {
        const textErr = await res.text();
        throw new Error(textErr || 'Gagal menghasilkan PDF');
      }

      const blob = await res.blob();
      clearInterval(timer);
      setProgressPercent(100);
      setProgressStatusText('Dokumen PDF siap!');

      setTimeout(() => {
        setShowProgressModal(false);
        setIsGeneratingPdf(false);
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setPdfTitle(`Rekapitulasi Jasa Medis (${selectedPeriode})`);
        setPdfFileName(`Rekap_Jasa_Medis_${(selectedPeriode || 'periode').replace(/\//g, '_')}.pdf`);
        setShowModalPdf(true);
      }, 350);

    } catch (err) {
      clearInterval(timer);
      setShowProgressModal(false);
      setIsGeneratingPdf(false);
      console.error(err);
      showMsg('Gagal membuat laporan PDF: ' + err.message, 'danger', 'Gagal Mencetak');
    }
  };

  // ── Handler Cetak PDF Slip Dokter (Format VB SUB printitslip - Opsi Per Dokter atau Seluruh Dokter) ──
  const handleCetakSlipPdf = async (isAll = false) => {
    if (!isAll && !selectedRow) {
      showMsg('Pilih salah satu dokter pada tabel terlebih dahulu untuk mencetak slip per dokter!', 'warning', 'Pilih Dokter');
      return;
    }
    if (isAll && (!selectedPeriode || dataJasa.length === 0)) {
      showMsg('Tidak ada data dokter pada periode ini untuk dicetak!', 'warning', 'Data Kosong');
      return;
    }

    setShowModalPilihSlip(false);
    setIsGeneratingPdf(true);
    setShowProgressModal(true);
    setProgressPercent(20);
    setProgressStatusText(isAll 
      ? `Mempersiapkan slip seluruh dokter (${summaryTotals.count || dataJasa.length} dokter) periode ${selectedPeriode}...` 
      : `Menyiapkan file PDF Slip Jasa Dokter ${selectedRow.nm_dokter}...`
    );

    try {
      const res = await fetch(`${getApiUrl()}/cetak_slip_dokter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kd_dokter: isAll ? 'ALL' : selectedRow.kd_dokter,
          cetak_all: isAll,
          periode: selectedPeriode
        })
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'Gagal menghasilkan file PDF Slip Dokter');
      }

      const blob = await res.blob();
      setProgressPercent(100);
      setProgressStatusText(isAll ? 'Seluruh Dokumen Slip Dokter Siap!' : 'Slip Jasa Medis Siap!');
      setTimeout(() => {
        setShowProgressModal(false);
        setIsGeneratingPdf(false);
        setShowModalSlip(false);
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        if (isAll) {
          setPdfTitle(`Slip Jasa Medis - Seluruh Dokter (${summaryTotals.count || dataJasa.length} Dokter) - Periode ${selectedPeriode}`);
          setPdfFileName(`Slip_Jasa_SEMUA_DOKTER_${(selectedPeriode || 'periode').replace(/\//g, '_')}.pdf`);
        } else {
          setPdfTitle(`Slip Jasa Medis - ${selectedRow.nm_dokter} (${selectedPeriode})`);
          setPdfFileName(`Slip_Jasa_${selectedRow.kd_dokter}_${(selectedPeriode || 'periode').replace(/\//g, '_')}.pdf`);
        }
        setShowModalPdf(true);
      }, 300);
    } catch (err) {
      setShowProgressModal(false);
      setIsGeneratingPdf(false);
      showMsg('Gagal membuat PDF slip: ' + err.message, 'danger', 'Gagal Mencetak Slip');
    }
  };

  // ── Handler Tutup Transaksi / Buku ──
  const handleTutupBuku = () => {
    if (!selectedPeriode) {
      showMsg('Pilih periode bayar terlebih dahulu!', 'warning', 'Pilih Periode');
      return;
    }
    showConfirm(
      `Verifikasi dan ubah status tutup buku transaksi Jasa Dokter untuk periode ${selectedPeriode}?`,
      async () => {
        try {
          const res = await fetch(`${getApiUrl()}/tutup_jasamedis`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ periode: selectedPeriode })
          });
          const json = await res.json();
          if (json.status === 'success') {
            showMsg(json.message, 'success', 'Status Tutup Buku Diperbarui');
            fetchJasaMedis();
          } else {
            showMsg(json.message || 'Gagal mengubah status tutup buku', 'danger', 'Gagal');
          }
        } catch (err) {
          showMsg('Terjadi kesalahan koneksi: ' + err.message, 'danger', 'Kesalahan Server');
        }
      },
      'Konfirmasi Tutup Buku'
    );
  };

  // Terbilang Rupiah Helper
  const terbilang = (angka) => {
    const bil = Math.floor(Math.abs(Number(angka) || 0));
    const satuan = ['', 'Satu', 'Dua', 'Tiga', 'Empat', 'Lima', 'Enam', 'Tujuh', 'Delapan', 'Sembilan', 'Sepuluh', 'Sebelas'];
    if (bil < 12) return satuan[bil];
    if (bil < 20) return terbilang(bil - 10) + ' Belas';
    if (bil < 100) return terbilang(Math.floor(bil / 10)) + ' Puluh ' + terbilang(bil % 10);
    if (bil < 200) return 'Seratus ' + terbilang(bil - 100);
    if (bil < 1000) return terbilang(Math.floor(bil / 100)) + ' Ratus ' + terbilang(bil % 100);
    if (bil < 2000) return 'Seribu ' + terbilang(bil - 1000);
    if (bil < 1000000) return terbilang(Math.floor(bil / 1000)) + ' Ribu ' + terbilang(bil % 1000);
    if (bil < 1000000000) return terbilang(Math.floor(bil / 1000000)) + ' Juta ' + terbilang(bil % 1000000);
    if (bil < 1000000000000) return terbilang(Math.floor(bil / 1000000000)) + ' Miliar ' + terbilang(bil % 1000000000);
    return '';
  };

  // ── Helper: warna badge status ──
  const getStatusBadge = (flag) => {
    if (flag === 'X') return { bg: '#fef3c7', color: '#92400e', label: 'Sudah Tutup', icon: 'bi-lock-fill' };
    if (flag === 'V') return { bg: '#d1fae5', color: '#065f46', label: 'Aktif', icon: 'bi-unlock-fill' };
    return { bg: '#f1f5f9', color: '#64748b', label: 'Belum Proses', icon: 'bi-dash-circle' };
  };


  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', paddingBottom: '6px', fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif" }}>

      {/* ══ TOP NAVBAR (COMPACT) ══ */}
      <nav className="navbar navbar-expand px-3 py-1 text-white shadow-sm"
        style={{ background: 'linear-gradient(135deg, #002f3d 0%, #004d66 100%)', borderBottom: '2px solid #f59e0b' }}>
        <div className="container-fluid p-0 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <div className="rounded-2 p-1 d-flex align-items-center justify-content-center"
              style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', width: '30px', height: '30px' }}>
              <i className="bi bi-person-badge-fill text-warning fs-6"></i>
            </div>
            <div>
              <div className="d-flex align-items-center gap-2">
                <span className="fw-bold text-white" style={{ fontSize: '0.88rem', letterSpacing: '0.2px' }}>
                  SIMRS &mdash; Pembayaran Jasa Medis Dokter
                </span>
                <Badge bg="warning" text="dark" className="px-2 py-0 fw-bold rounded-pill" style={{ fontSize: '0.66rem' }}>
                  <i className="bi bi-calendar-check me-1"></i>Periode: {selectedPeriode || '-'}
                </Badge>
              </div>
              <span className="text-white-50 d-none d-sm-block" style={{ fontSize: '0.65rem' }}>
                <i className="bi bi-shield-check text-success me-1"></i>
                Modul Finance &amp; Accounting &bull; Rekap &amp; Verifikasi Nota Jasa Dokter
              </span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <div className="text-end d-none d-md-block px-2 py-0 rounded-2"
              style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '0.68rem' }}>
              <div className="text-light fw-medium">
                <i className="bi bi-calendar3 me-1 text-info"></i>{time.date}
              </div>
              <div className="text-warning fw-bold">
                <i className="bi bi-clock me-1"></i>{time.clock}
              </div>
            </div>
            <Button variant="outline-light" size="sm"
              className="fw-semibold shadow-sm d-flex align-items-center gap-1 px-2 py-1"
              style={{ fontSize: '0.74rem', borderRadius: '6px' }}
              onClick={() => navigate('/home')}>
              <i className="bi bi-house-door-fill text-warning"></i>
              <span>Menu Utama</span>
            </Button>
          </div>
        </div>
      </nav>

      <Container fluid className="mt-1 px-2">

        {/* ══ SUMMARY METRIC CARDS (COMPACT) ══ */}
        <Row className="g-1 mb-1">
          {[
            {
              label: 'Tarif Dasar Pajak',
              value: `Rp ${formatRp(summaryTotals.totTarif)}`,
              sub: `Koreksi: Rp ${formatRp(summaryTotals.totKoreksiTarif)}`,
              subColor: summaryTotals.totKoreksiTarif < 0 ? '#ef4444' : '#10b981',
              icon: 'bi-calculator-fill', color: '#3b82f6', bg: '#eff6ff',
            },
            {
              label: 'Total Jasa Dokter',
              value: `Rp ${formatRp(summaryTotals.totJasa)}`,
              sub: `Koreksi: Rp ${formatRp(summaryTotals.totKoreksiJasa)}`,
              subColor: summaryTotals.totKoreksiJasa < 0 ? '#ef4444' : '#10b981',
              icon: 'bi-heart-pulse-fill', color: '#10b981', bg: '#ecfdf5',
            },
            {
              label: 'Non Jasa & Koreksi',
              value: `Rp ${formatRp(summaryTotals.totNonJasa + summaryTotals.totKoreksiNonJasa)}`,
              sub: `Non Jasa 2: Rp ${formatRp(summaryTotals.totNonJasa2)}`,
              subColor: '#64748b',
              icon: 'bi-shield-check', color: '#0ea5e9', bg: '#f0f9ff',
            },
            {
              label: 'Take Home Pay (THP)',
              value: `Rp ${formatRp(summaryTotals.totDibayar2)}`,
              sub: `${summaryTotals.count} Dokter Terverifikasi`,
              subColor: '#10b981',
              icon: 'bi-wallet2', color: '#f59e0b', bg: '#fffbeb',
            },
          ].map((card, i) => (
            <Col lg={3} sm={6} key={i}>
              <Card className="border-0 shadow-sm h-100 bg-white"
                style={{ borderRadius: '8px', borderLeft: `3px solid ${card.color}` }}>
                <Card.Body className="px-2 py-1 d-flex align-items-center justify-content-between">
                  <div>
                    <span className="fw-semibold d-block text-uppercase"
                      style={{ fontSize: '0.58rem', letterSpacing: '0.4px', color: '#94a3b8' }}>
                      {card.label}
                    </span>
                    <h6 className="fw-bold mb-0 mt-0" style={{ fontSize: '0.82rem', color: card.color }}>
                      {card.value}
                    </h6>
                    <span style={{ fontSize: '0.58rem', color: card.subColor }}>{card.sub}</span>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: '30px', height: '30px', background: card.bg }}>
                    <i className={`bi ${card.icon} fs-6`} style={{ color: card.color }}></i>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* ══ FILTER & TOOLBAR PANEL (COMPACT) ══ */}
        <Card className="shadow-sm border-0 mb-1 bg-white" style={{ borderRadius: '8px' }}>
          <Card.Body className="py-2 px-2">
            <Row className="g-2 align-items-center mb-1">
              {/* Periode Bayar (Input Teks Tanggal Sekarang + Bisa Diedit + Picker Kalender) */}
              <Col lg={4} md={5} sm={12}>
                <div className="d-flex justify-content-between align-items-center mb-0">
                  <Form.Label className="fw-semibold text-secondary mb-0" style={{ fontSize: '0.70rem' }}>
                    <i className="bi bi-calendar3 me-1 text-primary"></i>PERIODE BAYAR
                  </Form.Label>
                  <div className="d-flex align-items-center gap-1">
                    {selectedPeriode === getTodayDmy() ? (
                      <Badge bg="info" className="px-1 py-0 fw-semibold text-dark" style={{ fontSize: '0.60rem' }}>
                        Hari Ini
                      </Badge>
                    ) : listPeriode.length > 0 && selectedPeriode === listPeriode[0] ? (
                      <Badge bg="success" className="px-1 py-0 fw-semibold" style={{ fontSize: '0.60rem' }}>
                        Terakhir
                      </Badge>
                    ) : null}
                  </div>
                </div>
                <InputGroup size="sm">
                  {/* Input Teks Editable DD/MM/YYYY */}
                  <Form.Control
                    type="text"
                    value={selectedPeriode}
                    onChange={(e) => setSelectedPeriode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        fetchJasaMedis(selectedPeriode);
                      }
                    }}
                    placeholder="DD/MM/YYYY"
                    className="fw-bold py-0"
                    style={{ fontSize: '0.80rem', color: '#002f3d', borderColor: '#cbd5e1', borderRadius: '6px 0 0 6px', height: '28px' }}
                    title="Ketik tanggal / periode bayar (DD/MM/YYYY) dan tekan Enter atau tombol PROSES"
                  />
                  
                  {/* Hidden date input untuk native calendar picker */}
                  <input
                    type="date"
                    ref={datePickerRef}
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: '1px', height: '1px' }}
                    value={dmyToYmd(selectedPeriode)}
                    onChange={(e) => handleCalendarChange(e.target.value)}
                  />
                  
                  {/* Tombol Kalender Popover */}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    title="Pilih tanggal dari kalender"
                    onClick={() => {
                      try {
                        datePickerRef.current?.showPicker();
                      } catch {
                        datePickerRef.current?.focus();
                      }
                    }}
                    className="px-2 py-0"
                    style={{ borderColor: '#cbd5e1', height: '28px' }}
                  >
                    <i className="bi bi-calendar-event text-primary"></i>
                  </Button>

                  {/* Tombol Cepat Hari Ini */}
                  <Button
                    variant={selectedPeriode === getTodayDmy() ? "primary" : "outline-secondary"}
                    size="sm"
                    title="Isi dengan tanggal sekarang (Hari Ini)"
                    onClick={() => {
                      const today = getTodayDmy();
                      setSelectedPeriode(today);
                      fetchJasaMedis(today);
                    }}
                    className="d-flex align-items-center gap-1 fw-semibold px-2 py-0"
                    style={{ fontSize: '0.70rem', height: '28px' }}
                  >
                    <i className="bi bi-clock"></i>
                    <span>Hari Ini</span>
                  </Button>

                  {/* Dropdown Periode Tersimpan */}
                  {listPeriode.length > 0 && (
                    <Dropdown as={ButtonGroup} size="sm">
                      <Dropdown.Toggle
                        split
                        variant={listPeriode.length > 0 && selectedPeriode === listPeriode[0] ? "success" : "outline-secondary"}
                        size="sm"
                        id="dropdown-periode-list"
                        title="Pilih dari daftar periode yang tersimpan di database"
                        className="px-2 py-0"
                        style={{ borderRadius: '0 6px 6px 0', height: '28px', fontSize: '0.72rem' }}
                      />
                      <Dropdown.Menu align="end" className="shadow border-0 py-1" style={{ maxHeight: '280px', overflowY: 'auto', fontSize: '0.78rem', minWidth: '180px', zIndex: 1050 }}>
                        <Dropdown.Header className="py-1 text-uppercase fw-bold text-muted" style={{ fontSize: '0.65rem' }}>
                          Periode Tersimpan
                        </Dropdown.Header>
                        {listPeriode.map((p, i) => (
                          <Dropdown.Item
                            key={p}
                            active={selectedPeriode === p}
                            onClick={() => {
                              setSelectedPeriode(p);
                              fetchJasaMedis(p);
                            }}
                            className="d-flex justify-content-between align-items-center py-1 px-3"
                          >
                            <span className="fw-semibold">{p}</span>
                            {i === 0 && <Badge bg="success" className="ms-2" style={{ fontSize: '0.6rem' }}>Terakhir</Badge>}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </InputGroup>
              </Col>

              {/* Search */}
              <Col lg={4} md={3.5} sm={12}>
                <Form.Label className="fw-semibold text-secondary mb-0" style={{ fontSize: '0.70rem' }}>
                  <i className="bi bi-search me-1"></i>CARI NAMA / KODE DOKTER
                </Form.Label>
                <InputGroup size="sm">
                  <Form.Control type="text" placeholder="Ketik nama atau kode dokter..."
                    value={searchDokter} onChange={(e) => setSearchDokter(e.target.value)}
                    className="py-0"
                    style={{ fontSize: '0.80rem', borderColor: '#cbd5e1', borderRadius: '6px 0 0 6px', height: '28px' }} />
                  {searchDokter && (
                    <Button variant="outline-secondary" onClick={() => setSearchDokter('')}
                      className="py-0"
                      style={{ borderRadius: '0 6px 6px 0', height: '28px' }}>
                      <i className="bi bi-x"></i>
                    </Button>
                  )}
                </InputGroup>
              </Col>

              {/* Selected doctor info */}
              <Col lg={4} md={3.5} sm={12}>
                <Form.Label className="fw-semibold text-secondary mb-0" style={{ fontSize: '0.70rem' }}>
                  <i className="bi bi-person-lines-fill me-1"></i>DOKTER TERPILIH
                </Form.Label>
                <div className="rounded border d-flex align-items-center px-2"
                  style={{
                    background: selectedRow ? '#eff6ff' : '#f8fafc',
                    borderColor: selectedRow ? '#93c5fd' : '#e2e8f0',
                    height: '28px', fontSize: '0.78rem'
                  }}>
                  {selectedRow ? (
                    <>
                      <i className="bi bi-person-check-fill text-primary me-1" style={{ fontSize: '0.80rem' }}></i>
                      <span className="fw-bold text-dark text-truncate">
                        [{selectedRow.kd_dokter}] {selectedRow.nm_dokter}
                      </span>
                      <Badge bg="success" className="ms-auto px-1 py-0 fw-semibold" style={{ fontSize: '0.60rem' }}>
                        Siap
                      </Badge>
                    </>
                  ) : (
                    <span className="text-muted fst-italic" style={{ fontSize: '0.74rem' }}>
                      <i className="bi bi-hand-index me-1"></i>Klik baris pada tabel untuk memilih dokter
                    </span>
                  )}
                </div>
              </Col>
            </Row>

            {/* ── TOOLBAR ACTIONS (SLIM) ── */}
            <div className="border-top pt-1 d-flex flex-wrap align-items-center justify-content-between gap-1">
              <div className="d-flex flex-wrap gap-1">
                <Button variant="primary" size="sm"
                  onClick={handleBukaDialogPajak}
                  className="fw-semibold d-flex align-items-center gap-1 shadow-sm px-2 py-0"
                  style={{ borderRadius: '6px', fontSize: '0.74rem', height: '27px', background: '#002f3d', borderColor: '#002f3d' }}
                  title="Buka dialog perhitungan dan laporan pajak dokter">
                  <i className="bi bi-calculator"></i>
                  Perhitungan dan Laporan Pajak Dokter
                </Button>

                <Button variant="outline-secondary" size="sm" disabled={isLoading}
                  onClick={() => fetchJasaMedis()}
                  className="fw-semibold d-flex align-items-center gap-1 shadow-sm px-2 py-0"
                  style={{ borderRadius: '6px', fontSize: '0.74rem', height: '27px' }}
                  title="Muat ulang data tabel">
                  {isLoading ? <Spinner size="sm" animation="border" style={{ width: '12px', height: '12px' }} /> : <i className="bi bi-arrow-repeat"></i>}
                </Button>

                <Button variant="outline-success" size="sm" onClick={handleOpenRincian}
                  className="fw-semibold d-flex align-items-center gap-1 shadow-sm px-2 py-0"
                  style={{ borderRadius: '6px', fontSize: '0.74rem', height: '27px' }}
                  title="Lihat rincian tindakan">
                  <i className="bi bi-card-checklist"></i>RINCIAN
                </Button>

                <Dropdown as={ButtonGroup} size="sm">
                  <Button variant="outline-info" size="sm" onClick={handleOpenSlip}
                    className="fw-semibold d-flex align-items-center gap-1 shadow-sm px-2 py-0"
                    style={{ borderRadius: '6px 0 0 6px', fontSize: '0.74rem', height: '27px', color: '#0369a1' }}
                    title="Cetak slip jasa dokter (Pilih opsi per dokter atau seluruh dokter)">
                    <i className="bi bi-receipt-cutoff"></i>SLIP DOKTER
                  </Button>
                  <Dropdown.Toggle split variant="outline-info" size="sm" id="dropdown-slip"
                    style={{ borderRadius: '0 6px 6px 0', height: '27px', color: '#0369a1', paddingLeft: '4px', paddingRight: '4px' }}
                    title="Opsi Cepat Cetak Slip" />
                  <Dropdown.Menu className="shadow border-0 py-1" style={{ fontSize: '0.8rem', minWidth: '250px', borderRadius: '8px', zIndex: 1050 }}>
                    <Dropdown.Header className="text-uppercase fw-bold text-muted py-1" style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}>
                      Opsi Cetak Slip ({selectedPeriode || '-'})
                    </Dropdown.Header>
                    <Dropdown.Item onClick={() => handleCetakSlipPdf(false)} disabled={!selectedRow} className="py-2 d-flex align-items-center gap-2">
                      <i className="bi bi-person-fill text-primary fs-6"></i>
                      <div>
                        <div className="fw-semibold">Cetak Dokter Terpilih</div>
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                          {selectedRow ? `${selectedRow.nm_dokter}` : '(Pilih dokter di tabel)'}
                        </div>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Divider className="my-1" />
                    <Dropdown.Item onClick={() => handleCetakSlipPdf(true)} disabled={!selectedPeriode || dataJasa.length === 0} className="py-2 d-flex align-items-center gap-2">
                      <i className="bi bi-people-fill text-success fs-6"></i>
                      <div>
                        <div className="fw-semibold text-success">Cetak Seluruh Dokter</div>
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                          {summaryTotals.count || dataJasa.length} Dokter (1 File PDF Multi-Halaman)
                        </div>
                      </div>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>

                <Button variant="outline-warning" size="sm" onClick={handleTutupBuku}
                  className="fw-semibold d-flex align-items-center gap-1 shadow-sm text-dark px-2 py-0"
                  style={{ borderRadius: '6px', fontSize: '0.74rem', height: '27px' }}
                  title="Tutup buku periode">
                  <i className="bi bi-folder-check"></i>TUTUP
                </Button>

                <Button variant="outline-danger" size="sm" disabled={isGeneratingPdf}
                  onClick={handleCetakLaporan}
                  className="fw-semibold d-flex align-items-center gap-1 shadow-sm px-2 py-0"
                  style={{ borderRadius: '6px', fontSize: '0.74rem', height: '27px' }}
                  title="Cetak PDF laporan">
                  <i className="bi bi-file-earmark-pdf-fill"></i>LAPORAN
                </Button>
              </div>

              <Button variant="danger" size="sm" onClick={() => navigate('/home')}
                className="fw-semibold d-flex align-items-center gap-1 shadow-sm text-white px-2 py-0"
                style={{ borderRadius: '6px', fontSize: '0.74rem', height: '27px' }}
                title="Kembali ke dashboard">
                <i className="bi bi-box-arrow-left"></i>KELUAR
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* ══ MAIN DATA TABLE ══ */}
        <Card className="shadow-sm border-0 bg-white overflow-hidden mb-1" style={{ borderRadius: '8px' }}>
          {/* Table header bar */}
          <div className="px-2 py-1 d-flex align-items-center justify-content-between border-bottom"
            style={{ background: '#f8fafc' }}>
            <span className="fw-bold text-dark d-flex align-items-center gap-1" style={{ fontSize: '0.80rem' }}>
              <i className="bi bi-table text-primary"></i>
              Daftar Jasa Medis Dokter
              {filteredData.length > 0 && (
                <span className="badge bg-primary rounded-pill px-2 py-0" style={{ fontSize: '0.62rem' }}>
                  {filteredData.length} dokter
                </span>
              )}
            </span>
            <span className="text-muted" style={{ fontSize: '0.68rem' }}>
              <i className="bi bi-hand-index me-1"></i>Klik baris untuk memilih dokter
            </span>
          </div>

          <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 280px)', minHeight: '220px' }}>
            <Table hover size="sm" className="mb-0 text-nowrap align-middle table-compact" style={{ fontSize: '0.75rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                {/* Row 1 - group headers */}
                <tr className="text-center"
                  style={{ background: '#002f3d', color: '#fff', fontSize: '0.71rem' }}>
                  <th rowSpan={2} style={{ verticalAlign: 'middle', width: '38px', borderRight: '1px solid rgba(255,255,255,0.1)', padding: '5px 6px' }}>NO</th>
                  <th rowSpan={2} style={{ verticalAlign: 'middle', width: '60px', borderRight: '1px solid rgba(255,255,255,0.1)', padding: '5px 6px' }}>KODE</th>
                  <th rowSpan={2} style={{ verticalAlign: 'middle', minWidth: '190px', textAlign: 'left', borderRight: '1px solid rgba(255,255,255,0.1)', padding: '5px 8px' }}>NAMA DOKTER</th>
                  <th colSpan={2} style={{ background: '#004d66', borderRight: '1px solid rgba(255,255,255,0.1)', padding: '4px 6px' }}>TARIF DASAR PAJAK</th>
                  <th colSpan={2} style={{ background: '#026280', borderRight: '1px solid rgba(255,255,255,0.1)', padding: '4px 6px' }}>JASA DOKTER</th>
                  <th colSpan={2} style={{ background: '#005f80', borderRight: '1px solid rgba(255,255,255,0.1)', padding: '4px 6px' }}>NON JASA</th>
                  <th rowSpan={2} style={{ verticalAlign: 'middle', width: '110px', background: '#004d66', borderRight: '1px solid rgba(255,255,255,0.1)', padding: '5px 6px' }}>
                    JUMLAH<br /><small style={{ fontWeight: 'normal', opacity: 0.75 }}>[JASA+NON]</small>
                  </th>
                  <th rowSpan={2} style={{ verticalAlign: 'middle', width: '110px', background: '#0369a1', borderRight: '1px solid rgba(255,255,255,0.1)', padding: '5px 6px' }}>
                    BERSIH<br /><small style={{ fontWeight: 'normal', opacity: 0.75 }}>[DIBAYAR]</small>
                  </th>
                  <th rowSpan={2} style={{ verticalAlign: 'middle', width: '92px', background: '#b45309', borderRight: '1px solid rgba(255,255,255,0.1)', padding: '5px 6px' }}>
                    PAJAK<br /><small style={{ fontWeight: 'normal', opacity: 0.75 }}>[Rp]</small>
                  </th>
                  <th rowSpan={2} style={{ verticalAlign: 'middle', width: '92px', background: '#9f1239', borderRight: '1px solid rgba(255,255,255,0.1)', padding: '5px 6px' }}>
                    PINJAMAN<br /><small style={{ fontWeight: 'normal', opacity: 0.75 }}>[Rp]</small>
                  </th>
                  <th rowSpan={2} style={{ verticalAlign: 'middle', width: '110px', background: '#065f46', padding: '5px 6px' }}>
                    TAKE HOME<br /><small style={{ fontWeight: 'normal', opacity: 0.75 }}>[THP]</small>
                  </th>
                </tr>
                {/* Row 2 - sub-column labels */}
                <tr className="text-center"
                  style={{ background: '#003b4d', color: '#94a3b8', fontSize: '0.66rem' }}>
                  <th style={{ width: '95px', borderRight: '1px solid rgba(255,255,255,0.08)', padding: '3px 6px' }}>Sebelum Koreksi</th>
                  <th style={{ width: '85px', borderRight: '1px solid rgba(255,255,255,0.08)', padding: '3px 6px' }}>Koreksi</th>
                  <th style={{ width: '95px', borderRight: '1px solid rgba(255,255,255,0.08)', padding: '3px 6px' }}>Sebelum Koreksi</th>
                  <th style={{ width: '85px', borderRight: '1px solid rgba(255,255,255,0.08)', padding: '3px 6px' }}>Koreksi/Tutup</th>
                  <th style={{ width: '95px', borderRight: '1px solid rgba(255,255,255,0.08)', padding: '3px 6px' }}>Sebelum Koreksi</th>
                  <th style={{ width: '85px', borderRight: '1px solid rgba(255,255,255,0.08)', padding: '3px 6px' }}>Koreksi/Dijamin</th>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={14} className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2 text-muted fw-semibold mb-0" style={{ fontSize: '0.84rem' }}>
                        Mengambil data jasa dokter periode {selectedPeriode}...
                      </p>
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="text-center py-5 text-muted">
                      <i className="bi bi-inbox d-block fs-1 mb-2 opacity-40"></i>
                      <span className="fw-semibold d-block" style={{ fontSize: '0.88rem' }}>
                        Tidak ada data jasa medis untuk periode <b className="text-primary">{selectedPeriode || '-'}</b>.
                      </span>
                      <div className="small mt-2">
                        {listPeriode.length > 0 && selectedPeriode !== listPeriode[0] ? (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="mt-1 fw-semibold shadow-sm"
                            style={{ fontSize: '0.76rem' }}
                            onClick={() => {
                              setSelectedPeriode(listPeriode[0]);
                              fetchJasaMedis(listPeriode[0]);
                            }}
                          >
                            <i className="bi bi-clock-history me-1"></i>
                            Buka Data Periode Terakhir ({listPeriode[0]})
                          </Button>
                        ) : (
                          <span>Pilih periode lain atau klik tombol PROSES.</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : pagedData.map((row, idx) => {
                  const globalIdx = (currentPage - 1) * rowsPerPage + idx;
                  const isSelected = selectedRow && selectedRow.kd_dokter === row.kd_dokter;
                  const sb = getStatusBadge(row.flag_blmtutup);
                  return (
                    <tr key={row.kd_dokter || idx}
                      onClick={() => setSelectedRow(row)}
                      style={{
                        background: isSelected ? '#dbeafe' : idx % 2 === 0 ? '#fff' : '#f8fafc',
                        cursor: 'pointer',
                        transition: 'background 0.12s',
                        outline: isSelected ? '2px solid #93c5fd' : 'none',
                        outlineOffset: '-2px',
                      }}>
                      <td className="text-center fw-semibold text-secondary" style={{ borderRight: '1px solid #e2e8f0' }}>{globalIdx + 1}</td>
                      <td className="text-center font-monospace fw-bold" style={{ borderRight: '1px solid #e2e8f0', color: '#0369a1', fontSize: '0.71rem' }}>{row.kd_dokter}</td>
                      <td style={{ borderRight: '1px solid #e2e8f0' }}>
                        <div className="d-flex align-items-center gap-1">
                          {isSelected && <i className="bi bi-caret-right-fill text-primary" style={{ fontSize: '0.65rem' }}></i>}
                          <span className="fw-semibold text-dark">{row.nm_dokter}</span>
                          <span className="ms-1 px-2 rounded-pill" style={{ fontSize: '0.59rem', background: sb.bg, color: sb.color, fontWeight: 600 }}>
                            <i className={`bi ${sb.icon} me-1`}></i>{sb.label}
                          </span>
                        </div>
                      </td>
                      {/* Tarif */}
                      <td className="text-end font-monospace" style={{ borderRight: '1px solid #e2e8f0' }}>{formatRp(row.tarif)}</td>
                      <td className={`text-end font-monospace ${Number(row.koreksitarif) < 0 ? 'text-danger' : ''}`} style={{ borderRight: '1px solid #e2e8f0' }}>{formatRp(row.koreksitarif)}</td>
                      {/* Jasa */}
                      <td className="text-end font-monospace fw-semibold text-success" style={{ borderRight: '1px solid #e2e8f0' }}>{formatRp(row.jasa)}</td>
                      <td className={`text-end font-monospace ${Number(row.koreksijasa) < 0 ? 'text-danger' : ''}`} style={{ borderRight: '1px solid #e2e8f0' }}>{formatRp(row.koreksijasa)}</td>
                      {/* Non Jasa */}
                      <td className="text-end font-monospace" style={{ borderRight: '1px solid #e2e8f0' }}>{formatRp(row.nonjasa)}</td>
                      <td className="text-end font-monospace" style={{ borderRight: '1px solid #e2e8f0' }}>{formatRp(row.koreksinonjasa)}</td>
                      {/* Jumlah */}
                      <td className="text-end font-monospace fw-bold text-dark" style={{ borderRight: '1px solid #e2e8f0' }}>{formatRp(row.jumlah)}</td>
                      {/* Bersih */}
                      <td className="text-end font-monospace fw-bold text-primary" style={{ borderRight: '1px solid #e2e8f0' }}>{formatRp(row.dibayar)}</td>
                      {/* Pajak */}
                      <td className="text-end font-monospace fw-semibold" style={{ borderRight: '1px solid #e2e8f0', color: '#b45309' }}>{formatRp(row.pajak)}</td>
                      {/* Pinjaman */}
                      <td className="text-end font-monospace text-danger" style={{ borderRight: '1px solid #e2e8f0' }}>{formatRp(Number(row.pinjaman || 0) + Number(row.koreksipinjaman || 0))}</td>
                      {/* THP */}
                      <td className="text-end font-monospace fw-bold text-success">{formatRp(row.dibayar2 || (row.dibayar - row.pajak - (Number(row.pinjaman || 0) + Number(row.koreksipinjaman || 0))))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* ══ PAGINATION BAR ══ */}
          {filteredData.length > 0 && (
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 px-2 py-1 border-top bg-white"
              style={{ fontSize: '0.74rem' }}>

              {/* Left: info */}
              <div className="text-muted d-flex align-items-center gap-2">
                <span>
                  Menampilkan <b>{Math.min((currentPage - 1) * rowsPerPage + 1, filteredData.length)}</b>–<b>{Math.min(currentPage * rowsPerPage, filteredData.length)}</b> dari <b>{filteredData.length}</b> dokter
                </span>
                <span className="text-muted">|</span>
                <span className="d-flex align-items-center gap-1">
                  Baris/hal:
                  <Form.Select size="sm" value={rowsPerPage}
                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="py-0"
                    style={{ width: '65px', fontSize: '0.72rem', borderColor: '#cbd5e1', borderRadius: '4px', display: 'inline-block', height: '24px' }}>
                    {[10, 15, 20, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                  </Form.Select>
                </span>
              </div>

              {/* Right: page buttons */}
              <div className="d-flex align-items-center gap-1">
                <button className="btn btn-sm btn-outline-secondary px-2 py-0"
                  style={{ borderRadius: '4px', fontSize: '0.72rem', height: '24px' }}
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)} title="Halaman pertama">
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button className="btn btn-sm btn-outline-secondary px-2 py-0"
                  style={{ borderRadius: '4px', fontSize: '0.72rem', height: '24px' }}
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)} title="Sebelumnya">
                  <i className="bi bi-chevron-left"></i>
                </button>

                {/* Page number pills */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((item, i) =>
                    item === '...' ? (
                      <span key={`ellipsis-${i}`} className="px-1 text-muted">…</span>
                    ) : (
                      <button key={item}
                        className={`btn btn-sm px-2 py-0 ${currentPage === item ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{ borderRadius: '4px', fontSize: '0.72rem', minWidth: '28px', height: '24px' }}
                        onClick={() => setCurrentPage(item)}>
                        {item}
                      </button>
                    )
                  )
                }

                <button className="btn btn-sm btn-outline-secondary px-2 py-0"
                  style={{ borderRadius: '4px', fontSize: '0.72rem', height: '24px' }}
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)} title="Berikutnya">
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button className="btn btn-sm btn-outline-secondary px-2 py-0"
                  style={{ borderRadius: '4px', fontSize: '0.72rem', height: '24px' }}
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)} title="Halaman terakhir">
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          )}

          {/* ══ TOTAL FOOTER BAR ══ */}
          <div style={{ background: '#002f3d', color: '#fff', borderTop: '2px solid #f59e0b', padding: '4px 12px' }}>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <span className="fw-bold" style={{ fontSize: '0.82rem' }}>
                <i className="bi bi-calculator text-warning me-2"></i>
                TOTAL &mdash; {summaryTotals.count} DOKTER
              </span>
              <div className="d-flex flex-wrap gap-3" style={{ fontSize: '0.75rem' }}>
                {[
                  { label: 'Tarif Dasar', val: summaryTotals.totTarif, color: '#7dd3fc' },
                  { label: 'Jasa', val: summaryTotals.totJasa, color: '#6ee7b7' },
                  { label: 'Non Jasa', val: summaryTotals.totNonJasa + summaryTotals.totKoreksiNonJasa, color: '#fcd34d' },
                  { label: 'Jumlah', val: summaryTotals.totJumlah, color: '#e2e8f0' },
                  { label: 'Bersih', val: summaryTotals.totDibayar, color: '#fbbf24', bold: true },
                  { label: 'THP', val: summaryTotals.totDibayar2, color: '#4ade80', bold: true },
                ].map((t, i) => (
                  <div key={i} className="text-center">
                    <div style={{ color: '#94a3b8', fontSize: '0.65rem' }}>{t.label}</div>
                    <div className={`font-monospace ${t.bold ? 'fw-bold' : ''}`} style={{ color: t.color }}>
                      Rp {formatRp(t.val)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

      </Container>

      {/* ══════════════════════════════════════════════════════
          MODAL: RINCIAN TINDAKAN JASA DOKTER
      ══════════════════════════════════════════════════════ */}
      <Modal show={showModalRincian} onHide={() => setShowModalRincian(false)}
        size="xl" centered backdrop="static" keyboard contentClassName="border-0 shadow-lg"
        style={{ borderRadius: '16px' }}>
        <Modal.Header closeButton className="py-2 px-3"
          style={{ background: '#002f3d', color: '#fff', borderBottom: '2px solid #f59e0b' }}>
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-card-checklist text-warning fs-5"></i>
            <div>
              <Modal.Title className="fw-bold fs-6 mb-0 text-white">RINCIAN TINDAKAN &amp; JASA DOKTER</Modal.Title>
              <div className="text-white-50" style={{ fontSize: '0.7rem' }}>
                Dokter: <b>{selectedRow ? `[${selectedRow.kd_dokter}] ${selectedRow.nm_dokter}` : '-'}</b>
                &bull; Periode: <b>{selectedPeriode}</b>
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-3" style={{ background: '#f8fafc' }}>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div className="d-flex gap-2">
              <Badge bg="primary" className="px-3 py-2 rounded-pill fw-semibold">
                <i className="bi bi-list-ol me-1"></i>{filteredRincian.length} Tindakan
              </Badge>
              <Badge bg="success" className="px-3 py-2 rounded-pill fw-semibold">
                <i className="bi bi-cash-stack me-1"></i>
                Total Komisi: Rp {formatRp(filteredRincian.reduce((acc, c) => acc + Number(c.komisi || 0), 0))}
              </Badge>
            </div>
            <div style={{ maxWidth: '280px', width: '100%' }}>
              <InputGroup size="sm">
                <InputGroup.Text style={{ borderRadius: '8px 0 0 8px', background: '#f1f5f9', borderColor: '#cbd5e1' }}>
                  <i className="bi bi-search text-muted"></i>
                </InputGroup.Text>
                <Form.Control type="text" placeholder="Cari pasien / No RM / tindakan..."
                  value={searchRincian} onChange={(e) => setSearchRincian(e.target.value)}
                  style={{ fontSize: '0.82rem', borderColor: '#cbd5e1' }} />
                {searchRincian && (
                  <Button variant="outline-secondary" onClick={() => setSearchRincian('')}
                    style={{ borderRadius: '0 8px 8px 0' }}>
                    <i className="bi bi-x"></i>
                  </Button>
                )}
              </InputGroup>
            </div>
          </div>

          <div className="table-responsive rounded-3 border bg-white" style={{ maxHeight: '420px' }}>
            <Table hover size="sm" className="mb-0 text-nowrap" style={{ fontSize: '0.78rem' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#0369a1', color: '#fff', zIndex: 5 }}>
                <tr className="text-center">
                  <th style={{ width: '36px', padding: '7px 4px' }}>NO</th>
                  <th style={{ padding: '7px 6px' }}>NO KUITANSI</th>
                  <th style={{ padding: '7px 6px' }}>NO NOTA</th>
                  <th style={{ padding: '7px 6px' }}>NO RM</th>
                  <th style={{ textAlign: 'left', padding: '7px 10px' }}>NAMA PASIEN</th>
                  <th style={{ textAlign: 'left', padding: '7px 10px' }}>NAMA TINDAKAN</th>
                  <th style={{ textAlign: 'right', padding: '7px 10px' }}>KOMISI / JASA [Rp]</th>
                  <th style={{ padding: '7px 6px' }}>TGL BAYAR JASA</th>
                  <th style={{ padding: '7px 6px' }}>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingRincian ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4">
                      <Spinner animation="border" size="sm" variant="primary" />
                      <span className="ms-2 fw-semibold text-muted">Memuat rincian tindakan...</span>
                    </td>
                  </tr>
                ) : filteredRincian.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4 text-muted">
                      <i className="bi bi-inbox d-block fs-2 mb-2 opacity-50"></i>
                      Tidak ada rincian tindakan ditemukan.
                    </td>
                  </tr>
                ) : filteredRincian.map((item, idx) => (
                  <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td className="text-center text-muted">{idx + 1}</td>
                    <td className="text-center font-monospace" style={{ color: '#0369a1' }}>{item.no_kuitansi || '-'}</td>
                    <td className="text-center font-monospace">{item.no_nota || '-'}</td>
                    <td className="text-center font-monospace fw-bold text-dark">{item.no_medrec || '-'}</td>
                    <td className="fw-semibold text-dark">{item.nm_pasien || '-'}</td>
                    <td className="text-muted">{item.nm_tindakan || '-'}</td>
                    <td className="text-end font-monospace fw-bold text-success">{formatRp(item.komisi)}</td>
                    <td className="text-center text-muted" style={{ fontSize: '0.72rem' }}>{item.tgl_bayarjasa || item.tgl_bayar || '-'}</td>
                    <td className="text-center">
                      <span className="px-2 py-1 rounded-pill fw-semibold" style={{
                        fontSize: '0.64rem',
                        background: item.flag_bayar === 'V' ? '#d1fae5' : item.flag_bayar === 'X' ? '#fef3c7' : '#f1f5f9',
                        color: item.flag_bayar === 'V' ? '#065f46' : item.flag_bayar === 'X' ? '#92400e' : '#64748b',
                      }}>
                        {item.status_bayar || (item.flag_bayar === 'V' ? 'Sudah Dibayar' : 'Pending')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>

        <Modal.Footer className="py-2 px-3 bg-light d-flex justify-content-between">
          <span className="text-muted" style={{ fontSize: '0.73rem' }}>
            <i className="bi bi-info-circle me-1"></i>Data dari tabel komisi jasa medis RS
          </span>
          <Button variant="secondary" size="sm" className="fw-semibold rounded-2 d-flex align-items-center gap-1"
            onClick={() => setShowModalRincian(false)}>
            <i className="bi bi-x-lg"></i> Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════
          MODAL: OPSI PILIHAN CETAK SLIP DOKTER
      ══════════════════════════════════════════════════════ */}
      <Modal show={showModalPilihSlip} onHide={() => setShowModalPilihSlip(false)}
        centered backdrop="static" keyboard contentClassName="border-0 shadow-lg">
        <Modal.Header closeButton className="py-2 px-3"
          style={{ background: '#002f3d', color: '#fff', borderBottom: '2px solid #0284c7' }}>
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-receipt-cutoff text-info fs-5"></i>
            <div>
              <Modal.Title className="fw-bold fs-6 mb-0 text-white">Cetak Slip Jasa Dokter</Modal.Title>
              <div className="text-white-50" style={{ fontSize: '0.72rem' }}>
                Periode Bayar: <b className="text-warning">{selectedPeriode || '-'}</b>
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-3 bg-light">
          <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>
            Pilih metode pencetakan berkas slip jasa dokter:
          </p>

          <div className="d-flex flex-column gap-3">
            {/* OPSI 1: Cetak 1 Dokter Terpilih */}
            <Card className={`border shadow-sm transition-all ${selectedRow ? 'border-primary' : 'border-secondary opacity-75'}`}
              style={{ borderRadius: '8px' }}>
              <Card.Body className="p-3">
                <div className="d-flex align-items-start gap-3">
                  <div className="rounded-circle p-2 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ background: selectedRow ? '#e0f2fe' : '#f1f5f9', color: selectedRow ? '#0284c7' : '#94a3b8', width: '42px', height: '42px' }}>
                    <i className="bi bi-person-fill fs-5"></i>
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="fw-bold text-dark mb-1" style={{ fontSize: '0.9rem' }}>
                      Cetak Dokter Terpilih
                    </h6>
                    {selectedRow ? (
                      <div>
                        <div className="fw-semibold text-primary" style={{ fontSize: '0.82rem' }}>
                          {selectedRow.nm_dokter} ({selectedRow.kd_dokter})
                        </div>
                        <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                          Total Bersih (THP): <b className="text-success">Rp. {formatRp(selectedRow.dibayar2 || (selectedRow.dibayar - selectedRow.pajak - (Number(selectedRow.pinjaman || 0) + Number(selectedRow.koreksipinjaman || 0))))}</b>
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted fst-italic" style={{ fontSize: '0.75rem' }}>
                        <i className="bi bi-info-circle me-1 text-warning"></i>
                        Belum ada dokter yang dipilih pada tabel. Klik salah satu baris dokter di tabel terlebih dahulu jika ingin mencetak slip per dokter.
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-2 text-end">
                  <Button variant="primary" size="sm" disabled={!selectedRow}
                    onClick={() => handleCetakSlipPdf(false)}
                    className="fw-semibold px-3" style={{ fontSize: '0.78rem', borderRadius: '6px' }}>
                    <i className="bi bi-printer me-1"></i> Cetak Slip Dokter Terpilih
                  </Button>
                </div>
              </Card.Body>
            </Card>

            {/* OPSI 2: Cetak Seluruh Dokter */}
            <Card className="border border-success shadow-sm" style={{ borderRadius: '8px' }}>
              <Card.Body className="p-3">
                <div className="d-flex align-items-start gap-3">
                  <div className="rounded-circle p-2 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ background: '#dcfce7', color: '#16a34a', width: '42px', height: '42px' }}>
                    <i className="bi bi-people-fill fs-5"></i>
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="fw-bold text-dark mb-1" style={{ fontSize: '0.9rem' }}>
                        Cetak Seluruh Dokter (Batch)
                      </h6>
                      <Badge bg="success" style={{ fontSize: '0.68rem' }}>
                        {summaryTotals.count || dataJasa.length} Dokter
                      </Badge>
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
                      Mencetak sekaligus slip <b>seluruh dokter</b> periode <b>{selectedPeriode}</b> dalam 1 dokumen PDF multi-halaman (1 halaman A4 per dokter) untuk mempercepat proses pencetakan.
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-end">
                  <Button variant="success" size="sm" disabled={!selectedPeriode || dataJasa.length === 0}
                    onClick={() => handleCetakSlipPdf(true)}
                    className="fw-semibold px-3" style={{ fontSize: '0.78rem', borderRadius: '6px' }}>
                    <i className="bi bi-printer-fill me-1"></i> Cetak Seluruh Dokter ({summaryTotals.count || dataJasa.length} Slip)
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </div>
        </Modal.Body>

        <Modal.Footer className="py-2 px-3 bg-white border-top d-flex justify-content-end">
          <Button variant="outline-secondary" size="sm" className="fw-semibold rounded-2"
            onClick={() => setShowModalPilihSlip(false)}>
            Batal
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════
          MODAL: SLIP PEMBAYARAN (fallback/preview HTML)
      ══════════════════════════════════════════════════════ */}
      <Modal show={showModalSlip} onHide={() => setShowModalSlip(false)}
        size="lg" centered backdrop="static" keyboard contentClassName="border-0 shadow-lg">
        <Modal.Header closeButton className="py-2 px-3"
          style={{ background: '#002f3d', color: '#fff', borderBottom: '2px solid #f59e0b' }}>
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-receipt-cutoff text-warning fs-5"></i>
            <div>
              <Modal.Title className="fw-bold fs-6 mb-0 text-white">SLIP PEMBAYARAN JASA DOKTER</Modal.Title>
              <div className="text-white-50" style={{ fontSize: '0.7rem' }}>Periode: <b>{selectedPeriode}</b></div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-4 bg-white">
          {selectedRow ? (
            <div id="print-slip-area"
              style={{ border: '2px dashed #94a3b8', borderRadius: '10px', padding: '24px' }}>
              <div className="text-center border-bottom pb-3 mb-3">
                <h5 className="fw-bold mb-1" style={{ color: '#002f3d' }}>RS MARY CILEUNGSI HIJAU</h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.74rem' }}>
                  SLIP BUKTI PENERIMAAN HONORARIUM &amp; JASA MEDIS DOKTER
                </p>
                <div className="fw-semibold text-primary mt-1" style={{ fontSize: '0.8rem' }}>
                  Periode Pembayaran: {selectedPeriode}
                </div>
              </div>

              <Row className="mb-3" style={{ fontSize: '0.88rem' }}>
                <Col sm={6}>
                  <table className="w-100"><tbody>
                    <tr><td className="text-muted" style={{ width: '110px' }}>Kode Dokter</td>
                      <td className="fw-bold font-monospace">: {selectedRow.kd_dokter}</td></tr>
                    <tr><td className="text-muted">Nama Dokter</td>
                      <td className="fw-bold text-dark fs-6">: {selectedRow.nm_dokter}</td></tr>
                  </tbody></table>
                </Col>
                <Col sm={6}>
                  <table className="w-100"><tbody>
                    <tr><td className="text-muted" style={{ width: '110px' }}>Tanggal Cetak</td>
                      <td className="fw-semibold">: {time.date}</td></tr>
                    <tr><td className="text-muted">No. Slip</td>
                      <td className="fw-bold font-monospace text-primary">: {selectedRow.no_slip || `SLIP/${selectedRow.kd_dokter}/${selectedPeriode.replace(/\//g, '')}`}</td></tr>
                  </tbody></table>
                </Col>
              </Row>

              <Table bordered size="sm" className="mb-3" style={{ fontSize: '0.82rem' }}>
                <thead style={{ background: '#f1f5f9' }}>
                  <tr>
                    <th className="text-center" style={{ width: '36px' }}>NO</th>
                    <th>URAIAN KOMPONEN JASA</th>
                    <th className="text-end" style={{ width: '160px' }}>JUMLAH (RP)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    [1, 'Tarif Dasar Pelayanan Medis', selectedRow.tarif, ''],
                    [2, 'Koreksi Tarif Dasar', selectedRow.koreksitarif, ''],
                    [3, 'Jasa Medis Dokter', selectedRow.jasa, 'success'],
                    [4, 'Koreksi Jasa / Penyesuaian Tutup', selectedRow.koreksijasa, ''],
                    [5, 'Non Jasa / Dijamin', Number(selectedRow.nonjasa || 0) + Number(selectedRow.koreksinonjasa || 0), ''],
                    [6, 'Jumlah Kotor [Jasa + Non Jasa]', selectedRow.jumlah, 'bold'],
                    [7, 'Potongan Pajak Penghasilan (PPh)', selectedRow.pajak, 'danger'],
                    [8, 'Potongan Pinjaman / Kasbon', Number(selectedRow.pinjaman || 0) + Number(selectedRow.koreksipinjaman || 0), 'danger'],
                  ].map(([no, lbl, val, v]) => (
                    <tr key={no} style={{ background: no === 6 ? '#f1f5f9' : '#fff' }}>
                      <td className="text-center text-muted">{no}</td>
                      <td className={v === 'bold' ? 'fw-bold' : v === 'success' ? 'fw-semibold text-success' : v === 'danger' ? 'text-danger' : ''}>{lbl}</td>
                      <td className={`text-end font-monospace ${v === 'danger' ? 'text-danger' : v === 'success' ? 'fw-bold text-success' : v === 'bold' ? 'fw-bold' : ''}`}>
                        {v === 'danger' ? `(${formatRp(val)})` : formatRp(val)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: '#ecfdf5', borderTop: '2px solid #10b981' }}>
                    <td colSpan={2} className="fw-bold text-dark py-2">TOTAL DITERIMA BERSIH (TAKE HOME PAY)</td>
                    <td className="text-end font-monospace fw-bold text-success py-2 fs-6">
                      Rp {formatRp(selectedRow.dibayar2 || (selectedRow.dibayar - selectedRow.pajak - (Number(selectedRow.pinjaman || 0) + Number(selectedRow.koreksipinjaman || 0))))}
                    </td>
                  </tr>
                </tbody>
              </Table>

              <div className="p-2 rounded-2 bg-light border mb-4" style={{ fontSize: '0.78rem' }}>
                <span className="text-muted fw-semibold">Terbilang: </span>
                <span className="fw-bold text-primary fst-italic">
                  # {terbilang(selectedRow.dibayar2 || (selectedRow.dibayar - selectedRow.pajak - (Number(selectedRow.pinjaman || 0) + Number(selectedRow.koreksipinjaman || 0))))} Rupiah #
                </span>
              </div>

              <Row className="text-center mt-4" style={{ fontSize: '0.8rem' }}>
                <Col xs={6}>
                  <div className="text-muted mb-5">Dibuat Oleh,<br />Bendahara Pengeluaran</div>
                  <div className="fw-bold text-dark text-decoration-underline">( Bagian Keuangan )</div>
                </Col>
                <Col xs={6}>
                  <div className="text-muted mb-5">Diterima Oleh,<br />Dokter Bersangkutan</div>
                  <div className="fw-bold text-dark text-decoration-underline">( {selectedRow.nm_dokter} )</div>
                </Col>
              </Row>
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-person-x d-block fs-1 mb-2 opacity-50"></i>
              Pilih dokter pada tabel terlebih dahulu.
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="py-2 px-3 bg-light d-flex justify-content-between">
          <Button variant="outline-secondary" size="sm"
            className="fw-semibold rounded-2 d-flex align-items-center gap-1"
            onClick={() => setShowModalSlip(false)}>
            <i className="bi bi-x-lg"></i> Tutup
          </Button>
          <div className="d-flex gap-2">
            <Button variant="info" size="sm"
              className="fw-bold text-white d-flex align-items-center gap-1 shadow-sm rounded-2"
              onClick={handleCetakSlipPdf}>
              <i className="bi bi-file-earmark-pdf-fill"></i> Pratinjau PDF Slip
            </Button>
            <Button variant="primary" size="sm"
              className="fw-bold d-flex align-items-center gap-1 shadow-sm rounded-2"
              onClick={handlePrintSlipWindow}>
              <i className="bi bi-printer-fill"></i> Cetak (Fisik)
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════
          MODAL: PDF VIEWER
      ══════════════════════════════════════════════════════ */}
      <Modal show={showModalPdf} onHide={() => setShowModalPdf(false)}
        size="xl" centered backdrop="static" keyboard contentClassName="border-0 shadow-lg">
        <Modal.Header closeButton className="py-2 px-3"
          style={{ background: '#002f3d', color: '#fff', borderBottom: '2px solid #f59e0b' }}>
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-pdf-fill text-warning fs-5"></i>
            <div>
              <Modal.Title className="fw-bold fs-6 mb-0 text-white">
                {pdfTitle || 'Dokumen PDF Jasa Medis'}
              </Modal.Title>
              <div className="text-white-50" style={{ fontSize: '0.7rem' }}>
                Pratinjau Dokumen Resmi Rumah Sakit
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-0" style={{ height: '78vh', background: '#525659' }}>
          {pdfUrl ? (
            <iframe src={pdfUrl} title={pdfTitle} width="100%" height="100%"
              style={{ border: 'none' }} />
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100 text-white gap-2">
              <Spinner animation="border" variant="light" />
              <span>Memuat Pratinjau Dokumen PDF...</span>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="py-2 px-3 bg-light d-flex justify-content-between">
          <span className="text-muted" style={{ fontSize: '0.73rem' }}>
            <i className="bi bi-info-circle me-1"></i>
            Gunakan kontrol PDF browser untuk mencetak / mengunduh
          </span>
          <div className="d-flex gap-2">
            {pdfUrl && (
              <a href={pdfUrl} download={pdfFileName}
                className="btn btn-outline-primary btn-sm fw-semibold d-flex align-items-center gap-1 shadow-sm rounded-2">
                <i className="bi bi-download"></i> Unduh File
              </a>
            )}
            <Button variant="secondary" size="sm"
              className="fw-semibold rounded-2 d-flex align-items-center gap-1"
              onClick={() => setShowModalPdf(false)}>
              <i className="bi bi-x-lg"></i> Tutup
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════
          MODAL: PERHITUNGAN DAN LAPORAN PAJAK DOKTER
      ══════════════════════════════════════════════════════ */}
      <Modal show={showModalPajak} onHide={() => !isProcessingPajak && setShowModalPajak(false)} centered backdrop="static" keyboard={false} size="md">
        {/* Header Biru Tua Persis Screenshot */}
        <div style={{ background: '#002f3d', borderTopLeftRadius: '6px', borderTopRightRadius: '6px', padding: '10px 16px', borderBottom: '1px solid #001f29' }}>
          <div className="text-center fw-bold text-white text-uppercase" style={{ fontSize: '0.95rem', letterSpacing: '0.5px' }}>
            PERHITUNGAN DAN LAPORAN PAJAK DOKTER
          </div>
        </div>

        <Modal.Body className="p-3" style={{ background: '#cbd5e1', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px' }}>
          <div className="p-2 rounded bg-white shadow-sm border mb-1">
            {/* ── Row 1: Periode Pembayaran Jasa ── */}
            <div className="mb-2">
              <Row className="g-0 align-items-stretch border rounded overflow-hidden" style={{ borderColor: '#003852' }}>
                <Col xs={12} sm={5} className="d-flex align-items-center justify-content-start px-3 py-1" style={{ background: '#004b75' }}>
                  <span className="fw-bold text-white text-uppercase" style={{ fontSize: '0.74rem', letterSpacing: '0.3px' }}>
                    PERIODE PEMBAYARAN JASA
                  </span>
                </Col>
                <Col xs={12} sm={7}>
                  <Form.Control
                    type="text"
                    value={pajakPeriode}
                    onChange={(e) => setPajakPeriode(e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className="fw-bold border-0 rounded-0 px-3 py-1"
                    style={{ fontSize: '0.86rem', color: '#002f3d', height: '32px' }}
                    disabled={isProcessingPajak}
                  />
                </Col>
              </Row>
            </div>

            {/* ── Row 2: Lokasi File Laporan ── */}
            <div className="mb-3">
              <div className="text-center py-1 fw-bold text-white text-uppercase rounded-top" style={{ background: '#004b75', fontSize: '0.74rem', letterSpacing: '0.5px' }}>
                LOKASI FILE LAPORAN
              </div>
              <InputGroup size="sm" className="rounded-bottom overflow-hidden border border-top-0" style={{ borderColor: '#003852' }}>
                <Form.Control
                  type="text"
                  value={lokasiFileLaporan}
                  onChange={(e) => setLokasiFileLaporan(e.target.value)}
                  placeholder="C:\LAPORAN\PAJAK_DOKTER.pdf"
                  className="border-0 px-3 py-1 font-monospace"
                  style={{ fontSize: '0.80rem', color: '#334155', height: '32px' }}
                  disabled={isProcessingPajak}
                />
                <Button variant="light" className="border-start px-2 py-0 d-flex align-items-center" title="Pilih Lokasi" disabled={isProcessingPajak}>
                  <i className="bi bi-search text-secondary" style={{ fontSize: '0.85rem' }}></i>
                </Button>
              </InputGroup>
            </div>

            {/* ── Row 3: Progress Bar & Tombol Aksi ── */}
            <Row className="g-2 align-items-center">
              {/* Progress Bar */}
              <Col xs={12} sm={6}>
                <div className="p-1 rounded bg-light border d-flex align-items-center" style={{ height: '36px' }}>
                  <ProgressBar
                    now={pajakProgress}
                    label={`${pajakProgress}%`}
                    variant="primary"
                    animated={isProcessingPajak}
                    striped={isProcessingPajak}
                    className="w-100 fw-bold"
                    style={{ height: '24px', fontSize: '0.78rem', borderRadius: '4px' }}
                  />
                </div>
              </Col>

              {/* Tombol [PROSES] dan [KELUAR] */}
              <Col xs={12} sm={6} className="d-flex justify-content-end gap-2">
                <Button
                  variant="light"
                  size="sm"
                  disabled={isProcessingPajak}
                  onClick={handleJalankanProsesPajak}
                  className="fw-bold d-flex align-items-center justify-content-center gap-1 px-3 shadow-sm border"
                  style={{
                    height: '36px',
                    fontSize: '0.82rem',
                    background: '#f8fafc',
                    color: '#0f172a',
                    borderColor: '#94a3b8',
                    minWidth: '95px'
                  }}
                  title="Jalankan perhitungan pajak dokter dan buat laporan"
                >
                  {isProcessingPajak ? (
                    <Spinner size="sm" animation="border" style={{ width: '14px', height: '14px' }} />
                  ) : (
                    <i className="bi bi-printer-fill fs-6 text-dark"></i>
                  )}
                  <span><u>P</u>ROSES</span>
                </Button>

                <Button
                  variant="light"
                  size="sm"
                  disabled={isProcessingPajak}
                  onClick={() => setShowModalPajak(false)}
                  className="fw-bold d-flex align-items-center justify-content-center gap-1 px-3 shadow-sm border"
                  style={{
                    height: '36px',
                    fontSize: '0.82rem',
                    background: '#f8fafc',
                    color: '#0f172a',
                    borderColor: '#94a3b8',
                    minWidth: '95px'
                  }}
                  title="Keluar dari dialog"
                >
                  <i className="bi bi-x-circle-fill text-danger fs-6"></i>
                  <span><u>K</u>ELUAR</span>
                </Button>
              </Col>
            </Row>

            {/* Hasil Perhitungan & Opsi Cetak Laporan (jika proses selesai) */}
            {pajakResult && (
              <div className="mt-3 p-2 rounded border bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div style={{ fontSize: '0.80rem' }}>
                  <span className="badge bg-success me-2">Sukses</span>
                  <b>{pajakResult.count} Dokter</b> diproses | Total PPh 21: <b>Rp. {formatRp(pajakResult.total_pajak)}</b>
                </div>
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleCetakLaporanPajakPdf}
                  className="fw-semibold d-flex align-items-center gap-1 px-2 py-1 shadow-sm"
                  style={{ fontSize: '0.78rem' }}
                >
                  <i className="bi bi-file-earmark-pdf-fill"></i>
                  Buka / Cetak Laporan Pajak
                </Button>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* ══════════════════════════════════════════════════════
          MODAL: PROGRESS LOADING
      ══════════════════════════════════════════════════════ */}
      <Modal show={showProgressModal} centered backdrop="static" keyboard={false}
        contentClassName="border-0 shadow-lg" style={{ borderRadius: '16px' }}>
        <Modal.Body className="text-center p-5">
          <div className="mb-4 d-flex justify-content-center">
            <div style={{ position: 'relative', width: '64px', height: '64px' }}>
              <Spinner animation="border" variant="primary"
                style={{ width: '64px', height: '64px', borderWidth: '4px' }} />
              <i className="bi bi-file-earmark-pdf text-primary"
                style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: '1.4rem' }}>
              </i>
            </div>
          </div>
          <h6 className="fw-bold mb-2 text-dark" style={{ fontSize: '1rem' }}>Memproses Dokumen</h6>
          <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>
            {progressStatusText || 'Mohon tunggu beberapa saat...'}
          </p>
          <ProgressBar now={progressPercent} animated striped variant="primary"
            style={{ height: '8px', borderRadius: '6px' }} />
          <span className="text-muted mt-2 d-block" style={{ fontSize: '0.72rem' }}>
            {progressPercent}%
          </span>
        </Modal.Body>
      </Modal>

      {/* ══════════════════════════════════════════════════════
          MODAL: MESSAGEBOX MODERN
      ══════════════════════════════════════════════════════ */}
      <Modal show={messageBox.show} onHide={closeMsg} centered backdrop="static" keyboard
        contentClassName="border-0 shadow-lg" style={{ zIndex: 1060 }}>
        <div style={{ borderRadius: '16px', overflow: 'hidden', background: '#fff' }}>
          <div style={{
            height: '5px',
            background: messageBox.type === 'success' ? 'linear-gradient(90deg,#10b981,#059669)'
              : messageBox.type === 'danger' ? 'linear-gradient(90deg,#ef4444,#b91c1c)'
              : messageBox.type === 'warning' || messageBox.type === 'confirm' ? 'linear-gradient(90deg,#f59e0b,#d97706)'
              : 'linear-gradient(90deg,#3b82f6,#1d4ed8)'
          }} />
          <Modal.Body className="text-center p-4">
            <div className="d-flex justify-content-center mb-3">
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: messageBox.type === 'success' ? '#ecfdf5'
                  : messageBox.type === 'danger' ? '#fef2f2'
                  : messageBox.type === 'warning' || messageBox.type === 'confirm' ? '#fffbeb'
                  : '#eff6ff',
                border: `2px solid ${messageBox.type === 'success' ? '#a7f3d0'
                  : messageBox.type === 'danger' ? '#fecaca'
                  : messageBox.type === 'warning' || messageBox.type === 'confirm' ? '#fde68a'
                  : '#bfdbfe'}`,
              }}>
                {messageBox.type === 'success' && <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '2rem' }}></i>}
                {(messageBox.type === 'warning' || messageBox.type === 'confirm') && <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '2rem', color: '#d97706' }}></i>}
                {messageBox.type === 'danger' && <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '2rem' }}></i>}
                {messageBox.type === 'info' && <i className="bi bi-info-circle-fill text-primary" style={{ fontSize: '2rem' }}></i>}
              </div>
            </div>
            <h5 className="fw-bold mb-2" style={{ color: '#0f172a', fontSize: '1.1rem' }}>{messageBox.title}</h5>
            <p className="text-muted mb-4" style={{ fontSize: '0.88rem', lineHeight: '1.55', whiteSpace: 'pre-line' }}>
              {messageBox.message}
            </p>
            <div className="d-flex justify-content-center gap-2">
              {messageBox.type === 'confirm' && (
                <Button variant="outline-secondary" className="px-4 fw-semibold rounded-3"
                  style={{ fontSize: '0.82rem' }}
                  onClick={() => { closeMsg(); if (messageBox.onCancel) messageBox.onCancel(); }}>
                  {messageBox.cancelText || 'Batal'}
                </Button>
              )}
              <Button className="px-4 fw-bold text-white shadow-sm rounded-3"
                style={{
                  fontSize: '0.82rem', border: 'none', minWidth: '130px',
                  background: messageBox.type === 'success' ? 'linear-gradient(135deg,#10b981,#059669)'
                    : messageBox.type === 'danger' ? 'linear-gradient(135deg,#ef4444,#dc2626)'
                    : messageBox.type === 'warning' || messageBox.type === 'confirm' ? 'linear-gradient(135deg,#f59e0b,#d97706)'
                    : 'linear-gradient(135deg,#0284c7,#0369a1)',
                }}
                onClick={() => { const fn = messageBox.onConfirm; closeMsg(); if (fn) fn(); }}
                autoFocus>
                {messageBox.confirmText || 'OK, Mengerti'}
              </Button>
            </div>
          </Modal.Body>
        </div>
      </Modal>

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #print-slip-area, #print-slip-area * { visibility: visible !important; }
          #print-slip-area { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; border: none !important; padding: 0 !important; }
          .modal, .modal-backdrop { position: absolute !important; left: 0 !important; top: 0 !important; }
        }
        .table-compact th {
          padding: 5px 6px !important;
          line-height: 1.25 !important;
          vertical-align: middle !important;
        }
        .table-compact td {
          padding: 5px 6px !important;
          line-height: 1.25 !important;
          vertical-align: middle !important;
        }
        .table-hover tbody tr:hover td { background-color: rgba(59,130,246,0.07) !important; }
      `}</style>
    </div>
  );
}

export default BayarJasaMedis;
